'use client'

import React, { FC, useEffect, useState } from 'react'
import RichText from '@/components/RichText'
import { CMSLink } from '@/components/Link'
import Image from '@/components/Image'
import { useNearViewport } from '@/lib/client/hooks/useNearViewport'

export interface CoachCTALink {
  link: any // Link type from Payload
}

export interface CoachCTAProps {
  links?: CoachCTALink[]
  richText?: any // Lexical content
  media?: any // Media type from Payload
  height?: number
  className?: string
}

export const CoachCTA: FC<CoachCTAProps> = ({ 
  links, 
  richText, 
  media, 
  height,
  className = ''
}) => {
  const [bgX, setBgX] = useState(0)
  // The film strips are CSS backgrounds, which load as soon as the block renders; this block sits
  // below the fold, so they only get their image once it comes near the viewport.
  const { ref: sectionRef, near } = useNearViewport<HTMLDivElement>()

  useEffect(() => {
    let frameRequest: number

    function onFrame() {
      setBgX(prevBgX => prevBgX + 0.01)
      frameRequest = window.requestAnimationFrame(onFrame)
    }

    frameRequest = window.requestAnimationFrame(onFrame)
    return () => {
      window.cancelAnimationFrame(frameRequest)
    }
  }, [])

  return (
    // Full-bleed escape — same trick as HeroSection; needs overflow-x:hidden on body (globals.scss).
    <div
      ref={sectionRef}
      className={`relative overflow-hidden -my-[1px] left-1/2 -mx-[50vw] w-screen ${className}`}
    >
      {/* The background photo as a lazy, optimized image rather than a CSS `background-image`:
          that downloaded the full-size original (165 KB on the home page) during the first load,
          though the block is far below the fold. */}
      {typeof media === 'object' && media?.url && (
        <Image src={media.url} alt="" fill sizes="100vw" className="object-cover" />
      )}
      <div
        className={'relative py-[100px] sm:py-0 sm:h-dvh flex w-full'}
        style={{ maxHeight: height ?? undefined }}
      >
        <div className={'self-center flex w-full flex-col -rotate-1 scale-105'}>
          <div
            className={'h-[40px] w-full -mb-[1px]'}
            style={{
              backgroundImage: near ? `url(/media/film-strip-top-black.png)` : undefined,
              backgroundSize: 'contain',
              backgroundRepeat: 'repeat-x',
              backgroundPosition: `${bgX}rem 0`,
            }}
          />
          <div className={'bg-black text-white'}>
            <div className="container mb-8 my-4 py-4 z-10 relative flex items-center justify-center">
              <div className="max-w-[36.5rem] text-center">
                {richText && (
                  <RichText 
                    className="mb-6" 
                    content={richText} 
                    enableGutter={false} 
                  />
                )}
                <div className="flex justify-center gap-4">
                  {Array.isArray(links) &&
                    links.length > 0 &&
                    links.map(({ link }, i) => {
                      return (
                        <div key={i}>
                          <CMSLink {...link} />
                        </div>
                      )
                    })}
                </div>
              </div>
            </div>
          </div>
          <div
            className={'h-[40px] w-full -mt-[1px]'}
            style={{
              backgroundImage: near ? `url(/media/film-strip-bottom-black.png)` : undefined,
              backgroundSize: 'contain',
              backgroundRepeat: 'repeat-x',
              backgroundPosition: `${bgX}rem 0`,
            }}
          />
        </div>
      </div>
    </div>
  )
}